import { BonusClassification } from '@synergy/models';
import { ChipAutocomplete, FormikPoolDatePicker, PoolSelect, PoolTypography, SelectOption } from '@synergy/components';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormControlLabel, FormHelperText, Grid, Switch, Theme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';

import { CountryAutocomplete } from '../../../../components/country/country-autocomplete-list';
import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { getCountries } from '../../../../utils/get-country-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    headerWithToggle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    toggleContainer: {
        marginBottom: -10,
        position: 'absolute',
        right: 0,
    },
    toggleLeft: {
        marginRight: theme.spacing(3),
    },
    toggleRight: {
        marginRight: 0,
    },
}));

export const SupplierDetailConditionsContract = () => {
    const classes = useStyles();
    const locale = useAppSelector(bestLocaleSelector);
    const countries = getCountries(locale);
    const formikContext = useFormikContext<SupplierConditionsFormValues>();

    const bonusClassificationOptions: SelectOption[] = [
        {
            value: 'undefined',
            displayValue: <FormattedMessage id="app.select.option.noneSpecification" />,
        },
        {
            value: BonusClassification.Contract,
            displayValue: <FormattedMessage id="app.select.option.contract" />,
        },
        {
            value: BonusClassification.Bonus,
            displayValue: <FormattedMessage id="app.select.option.bonus" />,
        },
        {
            value: BonusClassification.BonusPlus,
            displayValue: <FormattedMessage id="app.select.option.bonusPlus" />,
        },
    ];

    return (
        <div>
            <Grid item={true} xs={12}>
                <PoolTypography styleType="sectionTitle" component="div" className={classes.headerWithToggle}>
                    <FormattedMessage id="app.contactsAndOrganizations.detail.contractTerms" />
                    <div className={classes.toggleContainer}>
                        <Field name="isVisibleInShop">
                            {({ field, form }: FieldProps<boolean>) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={
                                                form.values.bonusClassification === BonusClassification.Contract ||
                                                field.value
                                            }
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                            name={field.name}
                                            disabled={form.values.bonusClassification === BonusClassification.Contract}
                                        />
                                    }
                                    label={<FormattedMessage id="app.label.showInSupplierDirectory" />}
                                    className={classes.toggleLeft}
                                />
                            )}
                        </Field>
                        <Field name="isChargingSupplier">
                            {({ field }: FieldProps<boolean>) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                            name={field.name}
                                        />
                                    }
                                    label={<FormattedMessage id="app.label.isChargingSupplier" />}
                                    className={classes.toggleRight}
                                />
                            )}
                        </Field>
                    </div>
                </PoolTypography>
            </Grid>

            <Grid container={true} spacing={2}>
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={4}>
                        <Field name="bonusClassification">
                            {({ field, form, meta }: FieldProps<string>) => (
                                <>
                                    <PoolSelect
                                        fullWidth={true}
                                        options={bonusClassificationOptions}
                                        value={field.value}
                                        label={
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.bonusClassification" />
                                        }
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                        disallowDefaultValue={true}
                                    />
                                    {meta.error && meta.touched && (
                                        <FormHelperText error={true}>
                                            <FormattedMessage id={meta.error} />
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4}>
                        <FormikPoolDatePicker
                            name="bonusClassificationFrom"
                            label={
                                <FormattedMessage id="app.contactsAndOrganizations.detail.bonusClassificationFrom" />
                            }
                            readOnly={!formikContext.values.bonusClassification}
                        />
                    </Grid>
                    <Grid item={true} xs={4}>
                        <FormikPoolDatePicker
                            name="bonusClassificationTo"
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.bonusClassificationTo" />}
                            readOnly={!formikContext.values.bonusClassification}
                        />
                    </Grid>
                </Grid>

                {/* Where POOL has separate agreements per country — Würth
                    Böheimkirchen and Adolf Würth, for instance — the supplier
                    is reserved for the members of its countries. Empty means
                    every member, which is the normal case (key-user brief
                    A3.1). The member's own country decides, as it does for the
                    supplier lock. */}
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={8}>
                        <PoolTypography
                            typography="caption"
                            id="app.contactsAndOrganizations.detail.memberCountriesHint"
                        />
                        <Field name="memberCountryCodes">
                            {({ field, form }: FieldProps<string[]>) => (
                                <ChipAutocomplete<CountryAutocomplete>
                                    options={countries}
                                    value={countries.filter(country => (field.value ?? []).includes(country.code))}
                                    getOptionLabel={country => country.name}
                                    getChipLabel={country => country.name}
                                    onChange={(_event, value) =>
                                        form.setFieldValue(
                                            field.name,
                                            value.map(country => country.code)
                                        )
                                    }
                                    textfieldPlaceholder="app.contactsAndOrganizations.detail.addMemberCountry"
                                    noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};
